import { useEffect, useState } from "react";
 import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { purgeSilktideDom } from "@/hooks/useCookieConsent";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

 // List of known protected routes - used to determine if we should redirect to auth or show 404
 const KNOWN_PROTECTED_ROUTES = [
   '/dashboard',
   '/my-reviews',
   '/my-profile',
   '/templates',
   '/cycles',
   '/teams',
   '/staff-members',
   '/settings',
   '/plan-billing',
   '/support',
   '/manager-feedback',
   '/view-evaluations',
   '/analytics',
   '/calendar',
   '/roster',
   '/time-off-settings',
   '/leave-policies',
   '/leave-reports',
   '/survey-designer',
   '/poll-designer',
   '/survey-schedule',
   '/my-surveys',
   '/survey-response',
   '/survey-results',
   '/help-center',
   '/my-time-off',
   '/admin',
   '/assignments',
 ];
 
 // Check if the current path matches any known protected route
 const isKnownProtectedRoute = (pathname: string): boolean => {
   return KNOWN_PROTECTED_ROUTES.some(route => 
     pathname === route || pathname.startsWith(route + '/')
   );
 };
 
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const navigate = useNavigate();
   const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
 
   // Determine if this is a known route that requires auth
   const isKnownRoute = isKnownProtectedRoute(location.pathname);

  // Safety net: ensure no Silktide cookie banner remnants ever appear inside
  // the authenticated app. Runs on every navigation within a protected route.
  useEffect(() => {
    if (!isKnownRoute) return;
    purgeSilktideDom();
    const t1 = window.setTimeout(purgeSilktideDom, 100);
    const t2 = window.setTimeout(purgeSilktideDom, 600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [location.pathname, isKnownRoute]);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
         // Only redirect to auth if this is a known protected route
         if (!session && isKnownRoute) {
          navigate("/auth");
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
       // Only redirect to auth if this is a known protected route
       if (!session && isKnownRoute) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
   }, [navigate, isKnownRoute]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

   // If user is authenticated, show children (which includes the nested Routes with 404)
   // If user is not authenticated and this is NOT a known route, also show children (404 will render)
   // If user is not authenticated and this IS a known route, show nothing (redirect is happening)
   if (user) {
     return <>{children}</>;
   }
   
   // For unknown routes, let the children render (will show 404)
   if (!isKnownRoute) {
     return <>{children}</>;
   }
   
   // For known routes without auth, show nothing (redirect is in progress)
   return null;
};

export default ProtectedRoute;
